import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { workspaceService, workspaceDisplayName, canCreateAnotherWorkspace } from '../services/workspaceService';
import { eventBus } from '../events';
// AtlasWallLink is the door in the Atlas wall: the gate copy tells you the
// feature lives in Atlas; this opens the app-level Atlas pitch (z-[80], renders
// above Settings) so you can actually learn what that means.
export const AtlasWallLink = () => (_jsx("button", { type: "button", onClick: () => eventBus.emit('atlas:open', { demoActive: false, projectContext: false }), className: "text-xs font-semibold hover:underline", style: { color: 'var(--forest)' }, children: "See what Atlas includes \u2192" }));
// Layer-1 (identity) settings block: every workspace this identity holds a seat
// in, plus "New workspace" — the door that lets one person own several rooms
// (e.g. a personal workspace and "Symbiosis Workspace"). Lives on the Profile
// tab because workspace membership belongs to the PERSON, not to any one
// workspace. Creating auto-switches into the new workspace and reloads.
export const YourWorkspacesBlock = () => {
    const [workspaces, setWorkspaces] = useState(null);
    const [creating, setCreating] = useState(false);
    const [switching, setSwitching] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    useEffect(() => {
        let cancelled = false;
        workspaceService
            .listMyWorkspaces()
            .then((list) => { if (!cancelled)
            setWorkspaces(list); })
            .catch(() => { if (!cancelled)
            setWorkspaces([]); });
        return () => { cancelled = true; };
    }, []);
    const handleCreate = async () => {
        if (creating)
            return;
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Give the workspace a name.');
            return;
        }
        setCreating(true);
        setError('');
        try {
            await workspaceService.createWorkspace(trimmed);
            // The backend switches us into the new workspace; reload to land in it.
            window.location.reload();
        }
        catch (e) {
            setCreating(false);
            if (((e === null || e === void 0 ? void 0 : e.message) || '').includes('Atlas')) {
                setError('Additional workspaces are part of Atlas. Upgrade your current workspace to Atlas to create more.');
            }
            else {
                setError('Could not create the workspace. Please try again.');
            }
        }
    };
    const handleSwitch = async (w) => {
        if (w.isActive || switching || creating)
            return;
        setSwitching(w.id);
        setError('');
        try {
            await workspaceService.setActiveWorkspace(w.id);
            window.location.reload();
        }
        catch (_a) {
            setSwitching(null);
            setError('Could not switch workspace. Please try again.');
        }
    };
    return (_jsxs("div", { className: "pt-4 border-t", style: { borderColor: 'var(--pewter)' }, children: [_jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--charcoal)' }, children: "Your workspaces" }), _jsx("p", { className: "text-xs mb-2 mt-0.5", style: { color: 'var(--forest)' }, children: "The workspaces you belong to. Click one to switch into it." }), workspaces === null ? (_jsx("p", { className: "text-xs", style: { color: 'var(--forest)' }, children: "Loading\u2026" })) : (_jsx("ul", { className: "space-y-1", children: workspaces.map((w) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => handleSwitch(w), disabled: w.isActive || switching !== null, className: "w-full flex items-center gap-2 text-sm px-2.5 py-1.5 rounded-lg border text-left transition-colors", style: {
                            // Active row = charcoal fill + light text (the "you are here"
                            // signal, replacing the redundant check). Inactive = quiet
                            // outline that lights up on hover (which implies "switch").
                            color: w.isActive ? 'var(--sand)' : 'var(--charcoal)',
                            borderColor: w.isActive ? 'var(--charcoal)' : 'var(--pewter)',
                            backgroundColor: w.isActive ? 'var(--charcoal)' : 'transparent',
                            cursor: w.isActive ? 'default' : 'pointer',
                            opacity: switching && switching !== w.id ? 0.6 : 1,
                        }, onMouseEnter: (e) => {
                            if (!w.isActive && !switching)
                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)';
                        }, onMouseLeave: (e) => {
                            if (!w.isActive)
                                e.currentTarget.style.backgroundColor = 'transparent';
                        }, children: [_jsx("span", { className: "truncate", children: switching === w.id ? 'Switching…' : workspaceDisplayName(w) }), _jsx("span", { className: "ml-auto flex-shrink-0 text-[10px] px-1.5 py-px rounded-full font-medium", style: {
                                    backgroundColor: w.isActive ? 'var(--sand)' : 'var(--forest)',
                                    color: w.isActive ? 'var(--forest)' : 'var(--sand)',
                                }, children: w.role })] }) }, w.id))) })), showForm ? (_jsxs("div", { className: "mt-3 space-y-2", children: [_jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), onKeyDown: (e) => { if (e.key === 'Enter')
                            handleCreate(); }, placeholder: "Workspace name (e.g. Symbiosis Workspace)", maxLength: 80, autoFocus: true, disabled: creating, className: "w-full px-3 py-2 text-sm rounded-lg border focus:outline-none", style: { borderColor: 'var(--pewter)', backgroundColor: '#fff', color: 'var(--charcoal)' } }), _jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsx("button", { type: "button", onClick: () => { setShowForm(false); setError(''); setName(''); }, disabled: creating, className: "px-3 py-1.5 text-sm font-medium rounded-lg", style: { color: 'var(--charcoal)' }, children: "Cancel" }), _jsx("button", { type: "button", onClick: handleCreate, disabled: creating, className: "px-4 py-1.5 text-sm font-semibold rounded-lg disabled:opacity-60", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: creating ? 'Creating…' : 'Create workspace' })] })] })) : (_jsxs("button", { type: "button", onClick: () => {
                    // The Atlas wall meets you at the click — before any form.
                    if (workspaces && !canCreateAnotherWorkspace(workspaces)) {
                        setError('Additional workspaces are part of Atlas. Upgrade your current workspace to Atlas to create more.');
                        return;
                    }
                    setError('');
                    setShowForm(true);
                }, className: "mt-2 inline-flex items-center gap-1.5 text-xs font-medium hover:underline", style: { color: 'var(--forest)' }, children: [_jsx(Plus, { size: 13 }), "New workspace"] })), error && (_jsxs("div", { className: "space-y-1 mt-2", children: [_jsx("p", { className: "text-xs", style: { color: '#dc2626' }, children: error }), error.includes('Atlas') && _jsx(AtlasWallLink, {})] }))] }));
};
